import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaUser, FaEnvelope, FaPhone, FaComment, FaTimes } from 'react-icons/fa';

function ContactUs() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
    document: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formRef = useRef(null);
  const popupRef = useRef(null);
  const fileInputRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+?\d{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone number is invalid (10-15 digits)';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.document) {
      const allowedExtensions = ['pdf'];
      const maxFileSize = 5 * 1024 * 1024; // 5MB
      const ext = formData.document.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        newErrors.document = 'Document must be a PDF file';
      } else if (formData.document.size > maxFileSize) {
        newErrors.document = 'Document file size exceeds 5MB';
      }
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'document') {
      setFormData((prev) => ({ ...prev, document: files[0] || null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    setIsSubmitting(true);
    const data = new FormData();
    data.append('fullName', formData.fullName);
    data.append('email', formData.email);
    data.append('phone', formData.phone);
    data.append('message', formData.message);
    if (formData.document) {
      data.append('document', formData.document);
    }

    try {
      const response = await axios.post(
        process.env.REACT_APP_CONTACT_API_URL || 'https://tglcredit.co.ke/api/send-email.php',
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (response.data.type === 'success') {
        console.log('Email sent successfully:', response.data);
        setFormData({ fullName: '', email: '', phone: '', message: '', document: null });
        if (fileInputRef.current) fileInputRef.current.value = '';
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 5000);
      } else {
        setSubmitError(`Failed to send message: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      if (error.response && error.response.data.type === 'error') {
        setSubmitError(`Failed to send message: ${error.response.data.message}`);
      } else if (error.request) {
        setSubmitError('Failed to send message. Please check your connection or server availability.');
      } else {
        setSubmitError('Failed to send message. An unexpected error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ fullName: '', email: '', phone: '', message: '', document: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
    setErrors({});
    setShowSuccessPopup(false);
    setSubmitError('');
  };

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
  };

  useEffect(() => {
    if (showSuccessPopup && popupRef.current) {
      const focusableElements = popupRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
        if (e.key === 'Escape') {
          closeSuccessPopup();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      firstElement.focus();

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showSuccessPopup]);

  return (
    <div className="bg-gray-50 py-12 sm:py-16" data-aos="fade-up">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-primary-blue text-white rounded-lg py-8 px-4 text-center mb-8 animate-fade-up overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage: `url('/assets/images/contact-bg.jpg'), linear-gradient(#f3f4f6, #f3f4f6)`,
            }}
            onError={(e) => {
              e.target.style.backgroundImage = 'linear-gradient(#f3f4f6, #f3f4f6)';
              console.error('Failed to load image: /assets/images/contact-bg.jpg');
            }}
          ></div>
          <h1 className="relative text-3xl sm:text-4xl font-inter font-extrabold">
            We’d Love to Hear from You!
          </h1>
          <p className="relative text-base font-inter mt-3 max-w-2xl mx-auto">
            At TGL Credit, your financial goals are our priority. Reach out today!
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10 animate-fade-in">
          <div className="bg-white p-6 rounded-lg shadow-lg" data-aos="fade-up" data-aos-delay="100">
            <h2 className="text-2xl font-inter font-bold text-primary-blue mb-4">Get in Touch</h2>
            <p className="text-base font-inter text-gray-600 mb-4">
              Our team is ready to assist you with personalized financial solutions.
            </p>
            <ul className="space-y-3 text-base font-inter text-gray-700">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-red mr-2 mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <div>
                  <strong>Phone:</strong>
                  <br />
                  <a href="tel:0727226644" className="hover:text-primary-red">
                    0727 22 66 44
                  </a>
                  ,{' '}
                  <a href="tel:0705194409" className="hover:text-primary-red">
                    0705 19 44 09
                  </a>
                </div>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-red mr-2 mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <div>
                  <strong>Email:</strong>
                  <br />
                  info@tglcredit.co.ke
                </div>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-red mr-2 mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <div>
                  <strong>Address:</strong>
                  <br />
                  Kimathi Street, Nairobi
                </div>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-red mr-2 mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <div>
                  <strong>Building:</strong>
                  <br />
                  Pioneer House, 7th Floor Suite 4
                </div>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-red mr-2 mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <strong>Hours:</strong>
                  <br />
                  Monday to Friday: 8:00 AM - 5:00 PM
                  <br />
                  Saturday: 8:30 AM - 12:00 PM
                </div>
              </li>
            </ul>
            <div className="mt-4">
              <Link
                to="/branches"
                className="inline-block bg-primary-blue text-white font-inter font-medium px-4 py-2 rounded-lg hover:scale-105 hover:shadow-lg transition-all duration-300 text-sm focus:ring-2 focus:ring-primary-blue focus:ring-offset-2"
                aria-label="View our branches"
              >
                Find a Branch Near You
              </Link>
            </div>
          </div>
          <div
            className="bg-white glassmorphism p-6 rounded-lg shadow-lg"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <h2 className="text-2xl font-inter font-bold text-primary-blue mb-4">Send Us a Message</h2>
            {submitError && (
              <div
                className="bg-red-100 text-red-800 p-3 rounded-lg mb-4 animate-fade-in"
                role="alert"
                aria-live="polite"
              >
                {submitError}
              </div>
            )}
            <form ref={formRef} onSubmit={handleSubmit} noValidate role="form">
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-sm font-inter font-semibold text-primary-blue mb-1"
                  >
                    Your Name
                  </label>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 text-base border ${
                        errors.fullName ? 'border-primary-red' : 'border-gray-200'
                      } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 focus:scale-105 transition-all duration-300`}
                      aria-describedby={errors.fullName ? 'fullName-error' : undefined}
                      required
                    />
                  </div>
                  {errors.fullName && (
                    <p id="fullName-error" className="text-primary-red text-xs mt-1" role="alert">
                      {errors.fullName}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-inter font-semibold text-primary-blue mb-1"
                  >
                    Your Email
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 text-base border ${
                        errors.email ? 'border-primary-red' : 'border-gray-200'
                      } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 focus:scale-105 transition-all duration-300`}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      required
                    />
                  </div>
                  {errors.email && (
                    <p id="email-error" className="text-primary-red text-xs mt-1" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-inter font-semibold text-primary-blue mb-1"
                  >
                    Your Phone
                  </label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 text-base border ${
                        errors.phone ? 'border-primary-red' : 'border-gray-200'
                      } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 focus:scale-105 transition-all duration-300`}
                      aria-describedby={errors.phone ? 'phone-error' : undefined}
                      required
                    />
                  </div>
                  {errors.phone && (
                    <p id="phone-error" className="text-primary-red text-xs mt-1" role="alert">
                      {errors.phone}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="document"
                    className="block text-sm font-inter font-semibold text-primary-blue mb-1"
                  >
                    Upload Document (Optional, PDF only, max 5MB)
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      id="document"
                      name="document"
                      accept=".pdf"
                      onChange={handleChange}
                      ref={fileInputRef}
                      className={`w-full px-3 py-2 text-base border ${
                        errors.document ? 'border-primary-red' : 'border-gray-200'
                      } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 focus:scale-105 transition-all duration-300`}
                      aria-describedby={errors.document ? 'document-error' : undefined}
                    />
                  </div>
                  {errors.document && (
                    <p id="document-error" className="text-primary-red text-xs mt-1" role="alert">
                      {errors.document}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-inter font-semibold text-primary-blue mb-1"
                  >
                    Your Message
                  </label>
                  <div className="relative">
                    <FaComment className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 text-base border ${
                        errors.message ? 'border-primary-red' : 'border-gray-200'
                      } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 focus:scale-105 transition-all duration-300 resize-y`}
                      rows="4"
                      aria-describedby={errors.message ? 'message-error' : undefined}
                      required
                    ></textarea>
                  </div>
                  {errors.message && (
                    <p id="message-error" className="text-primary-red text-xs mt-1" role="alert">
                      {errors.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-white border border-gray-200 text-gray-700 font-inter font-medium px-4 py-2 rounded-lg hover:bg-gray-100 hover:shadow-md transition-all duration-300 text-sm focus:ring-2 focus:ring-primary-blue focus:ring-offset-2"
                  aria-label="Cancel form submission"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-primary-blue text-white font-inter font-medium px-4 py-2 rounded-lg hover:scale-105 hover:shadow-lg transition-all duration-300 text-sm focus:ring-2 focus:ring-primary-blue focus:ring-offset-2 flex items-center gap-2"
                  aria-label="Submit contact form"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
              {showSuccessPopup && (
                <div
                  ref={popupRef}
                  className="mt-4 bg-white glassmorphism border border-primary-blue/20 rounded-lg p-4 shadow-lg animate-fade-in"
                  role="alert"
                  aria-live="polite"
                  data-aos="zoom-in"
                >
                  <div className="flex justify-between items-center mb-1">
                    <h3 className="text-base font-inter font-semibold text-primary-blue">
                      Message Sent Successfully!
                    </h3>
                    <button
                      onClick={closeSuccessPopup}
                      className="text-gray-600 hover:text-primary-red focus:ring-2 focus:ring-primary-red focus:ring-offset-2 rounded-full"
                      aria-label="Close success popup"
                    >
                      <FaTimes className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm font-inter text-gray-600">
                    Thank you for contacting TGL Credit. We’ll respond within 24 hours.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
        <div className="bg-white glassmorphism p-6 rounded-lg shadow-lg mt-10" data-aos="fade-up" data-aos-delay="300">
          <h2 className="text-2xl font-inter font-bold text-primary-blue mb-4">Our Location</h2>
          <p className="text-base font-inter text-gray-600 mb-4">
            Visit us at Kimathi Street, Nairobi, in Pioneer House, 7th Floor Suite 4
          </p>
          <div className="relative w-full h-96 rounded-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.815850196602!2d36.81994631475709!3d-1.284102998687581!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f112f9b8b7e7b%3A0x3f4b8f9c6f2d0e1c!2sPioneer%20House%2C%20Kimathi%20St%2C%20Nairobi%2C%20Kenya!5e0!3m2!1sen!2ske!4v1697051234567!5m2!1sen!2ske"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Google Map of TGL Credit Limited, Pioneer House, Kimathi Street, Nairobi, Kenya"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;